import { useQuery } from 'react-query';

import { QueryEntity } from '../types';
import { FieldMappingResponse } from '../types/backend-dto-types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

interface UseGetRelativityFieldMappingOptions {
  workspaceId: number | null;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
}

const useGetRelativityFieldMapping = (options: UseGetRelativityFieldMappingOptions) => {
  const { workspaceId, enabled = true, refetchOnWindowFocus = false } = options;
  const { fetchConfigGET } = useGetFetchConfig();

  const getFieldMapping = async (): Promise<FieldMappingResponse | null> => {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    const url = `${APIBaseChronos}/api/relativity-mappings/workspaces/${workspaceId}`;
    const response = await fetch(url, fetchConfigGET);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch field mapping: ${response.status} ${response.statusText}`);
    }

    return response.json();
  };

  return useQuery([QueryEntity.RelativityFieldMapping, { workspaceId }], getFieldMapping, {
    enabled: enabled && workspaceId !== null,
    refetchOnWindowFocus,
    retry: (failureCount, error) => {
      if (error instanceof Error && (error.message.includes('401') || error.message.includes('403'))) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export default useGetRelativityFieldMapping;
